import {
    ArrowLeft,
    Banknote,
    Calendar,
    ChevronRight,
    Footprints,
    Heart,
    Pencil,
    Phone,
    Save,
    ShoppingBag,
    Star,
    StickyNote,
    Tag,
    Trash2,
    User,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    getCustomer,
    getCustomerBills,
    getCustomerStatistics,
    getPointHistory,
    updateCustomer,
    deleteCustomer,
} from '../../../api/customer.api';
import styles from '../../../assets/scss/pages/CustomerDetail.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import { getSocket } from '../../../services/socket.service';
import { formatCurrency, formatPhoneNumber, formatPoints } from '../../../utils/formatters';
import CustomerPackagesPanel from '../packages/CustomerPackagesPanel';

const TYPE_OPTIONS = [
    { value: 'new', label: 'Khách mới' },
    { value: 'normal', label: 'Khách thường' },
    { value: 'vip', label: 'Khách VIP' },
];

const TYPE_LABELS = { new: 'Khách mới', normal: 'Khách thường', vip: 'Khách VIP' };

function formatDateTime(dateStr) {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
}

function formatRelativeDate(dateStr) {
    if (!dateStr) return '';
    const now = new Date();
    const d = new Date(dateStr);
    const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
    if (diffDays === 0) return 'HÔM NAY';
    if (diffDays === 1) return 'HÔM QUA';
    return `${diffDays} NGÀY TRƯỚC`;
}

function formatCustomerCode(id) {
    return `KH${String(id).padStart(6, '0')}`;
}

function formatBillCode(bill) {
    return bill.code || `HD${String(bill.id).padStart(6, '0')}`;
}

const STATUS_MAP = {
    paid: { label: 'Đã thanh toán', class: 'paid' },
    pending: { label: 'Chưa thanh toán', class: 'pending' },
    partial: { label: 'Thanh toán một phần', class: 'partial' },
    cancelled: { label: 'Đã huỷ', class: 'cancelled' },
};

export default function CustomerDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { hasPermission } = usePermissions();
    const canUpdate = hasPermission(PERMISSIONS.CUSTOMER.UPDATE);
    const canDelete = hasPermission(PERMISSIONS.CUSTOMER.DELETE);
    const canViewPhone = hasPermission(PERMISSIONS.CUSTOMER.VIEW_PHONE);
    // Thẻ gói: gate theo SERVICE.VIEW_LIST (giống app: nút "Thẻ dịch vụ" ở ListCustomer)
    const canViewPackages = hasPermission(PERMISSIONS.SERVICE.VIEW_LIST);

    const [customer, setCustomer] = useState(null);
    const [statistics, setStatistics] = useState({ totalSales: 0, visitCount: 0, totalOwed: 0 });
    const [recentBills, setRecentBills] = useState([]);
    const [pointHistory, setPointHistory] = useState([]);
    const [loading, setLoading] = useState(true);

    // Edit mode
    const [isEditing, setIsEditing] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // Tabs: bills | points
    const [activeTab, setActiveTab] = useState(location.state?.tab || 'bills');

    const isKhachLe = customer?.name === 'Khách lẻ';

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const [custRes, statsRes, billsRes, pointsRes] = await Promise.all([
                getCustomer(id),
                getCustomerStatistics(id).catch(() => ({ data: {} })),
                getCustomerBills(id, { limit: 5 }),
                getPointHistory(id, { page: 1, limit: 1000 }).catch(() => ({ data: {} })),
            ]);

            const custData = custRes.data.customer || custRes.data;
            setCustomer(custData);
            setEditForm({
                name: custData.name || '',
                phone: custData.phone || '',
                point: custData.point || 0,
                type: custData.type || 'normal',
                noteLike: custData.noteLike || '',
                noteInternal: custData.noteInternal || '',
            });

            const statsData = statsRes.data || {};
            setStatistics({
                totalSales: statsData.totalSales || 0,
                visitCount: statsData.visitCount || 0,
                totalOwed: statsData.totalOwed || 0,
            });

            const bills = billsRes.data.data || billsRes.data.bills || billsRes.data || [];
            setRecentBills(Array.isArray(bills) ? bills.slice(0, 5) : []);

            const points = pointsRes.data?.data || pointsRes.data || [];
            setPointHistory(Array.isArray(points) ? points : []);
        } catch (err) {
            console.error('Error fetching customer:', err);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    // Socket realtime
    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;

        const onStatsUpdated = () => {
            getCustomerStatistics(id)
                .then(({ data }) => {
                    setStatistics({
                        totalSales: data.totalSales || 0,
                        visitCount: data.visitCount || 0,
                        totalOwed: data.totalOwed || 0,
                    });
                })
                .catch(() => {});
            getCustomerBills(id, { limit: 5 })
                .then(({ data }) => {
                    const bills = data.data || data.bills || data || [];
                    setRecentBills(Array.isArray(bills) ? bills.slice(0, 5) : []);
                })
                .catch(() => {});
        };

        socket.on('statistics.updated', onStatsUpdated);
        socket.on('bill.created', onStatsUpdated);
        socket.on('bill.updated', onStatsUpdated);

        return () => {
            socket.off('statistics.updated', onStatsUpdated);
            socket.off('bill.created', onStatsUpdated);
            socket.off('bill.updated', onStatsUpdated);
        };
    }, [id]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await updateCustomer(id, {
                name: editForm.name,
                phone: editForm.phone,
                point: Number(editForm.point),
                type: editForm.type,
                noteLike: editForm.noteLike,
                noteInternal: editForm.noteInternal,
            });
            setCustomer((prev) => ({ ...prev, ...editForm, point: Number(editForm.point) }));
            setIsEditing(false);
            setSuccessMessage('Cập nhật thành công');
            setShowSuccess(true);
            setTimeout(() => setShowSuccess(false), 1000);
        } catch (err) {
            const msg = err.response?.data?.message || 'Cập nhật thất bại';
            alert(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setEditForm({
            name: customer.name || '',
            phone: customer.phone || '',
            point: customer.point || 0,
            type: customer.type || 'normal',
            noteLike: customer.noteLike || '',
            noteInternal: customer.noteInternal || '',
        });
        setIsEditing(false);
    };

    const handleDelete = async () => {
        if (!window.confirm(`Bạn có chắc muốn xoá khách hàng "${customer.name}" không?`)) return;
        try {
            await deleteCustomer(id);
            setSuccessMessage('Xoá khách hàng thành công');
            setShowSuccess(true);
            setTimeout(() => navigate('/admin/customers', { replace: true }), 1200);
        } catch (err) {
            alert(err.response?.data?.message || 'Xoá khách hàng thất bại');
        }
    };

    const filteredPoints = pointHistory.filter((t) => t.billId).slice(0, 5);


    if (loading) {
        return (
            <div className={styles.page}>
                <div className={styles.header}>
                    <div className={styles.skeleton} style={{ width: 38, height: 38, borderRadius: 10 }} />
                    <div className={styles.skeleton} style={{ width: 240, height: 28, borderRadius: 8 }} />
                </div>
                <div className={styles.layout}>
                    <div className={styles.layoutLeft}>
                        <div className={styles.card}>
                            <div style={{ padding: 24 }}>
                                {[1, 2, 3, 4].map((i) => (
                                    <div key={i} style={{ marginBottom: 18 }}>
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '40%', height: 14, borderRadius: 6, marginBottom: 6 }}
                                        />
                                        <div
                                            className={styles.skeleton}
                                            style={{ width: '70%', height: 18, borderRadius: 6 }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.layoutRight}>
                        <div className={styles.card}>
                            <div style={{ padding: 24 }}>
                                {[1, 2, 3].map((i) => (
                                    <div
                                        key={i}
                                        className={styles.skeleton}
                                        style={{ width: '100%', height: 60, borderRadius: 10, marginBottom: 12 }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!customer) {
        return <div className={styles.emptyPage}>Không tìm thấy khách hàng</div>;
    }

    return (
        <div className={styles.page}>
            {/* ===== HEADER ===== */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <button className={styles.backBtn} onClick={() => navigate(-1)}>
                        <ArrowLeft />
                    </button>
                    <div>
                        <h1 className={styles.headerTitle}>{customer.name}</h1>
                        <span className={styles.headerCode}>{formatCustomerCode(customer.id)}</span>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    {isEditing ? (
                        <>
                            <button className={styles.actionBtn} onClick={handleCancel}>
                                <X /> Huỷ
                            </button>
                            <button
                                className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                                onClick={handleSave}
                                disabled={saving}
                            >
                                <Save /> {saving ? 'Đang lưu...' : 'Lưu'}
                            </button>
                        </>
                    ) : (
                        !isKhachLe && (
                            <>
                                {canUpdate && (
                                    <button
                                        className={`${styles.actionBtn} ${styles.actionBtnPrimary}`}
                                        onClick={() => setIsEditing(true)}
                                    >
                                        <Pencil /> Chỉnh sửa
                                    </button>
                                )}
                                {canDelete && (
                                    <button
                                        className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                                        onClick={handleDelete}
                                    >
                                        <Trash2 /> Xoá
                                    </button>
                                )}
                            </>
                        )
                    )}
                </div>
            </div>

            {/* ===== HIGHLIGHTS ===== */}
            <div className={styles.highlights}>
                <div className={styles.highlight}>
                    <div className={`${styles.highlightIcon} ${styles.highlightIconBlue}`}>
                        <ShoppingBag />
                    </div>
                    <div className={styles.highlightContent}>
                        <span className={styles.highlightLabel}>Tổng bán</span>
                        <span className={styles.highlightValue}>{formatCurrency(statistics.totalSales)}</span>
                    </div>
                </div>
                <div className={styles.highlight}>
                    <div className={`${styles.highlightIcon} ${styles.highlightIconGreen}`}>
                        <Footprints />
                    </div>
                    <div className={styles.highlightContent}>
                        <span className={styles.highlightLabel}>Ghé thăm</span>
                        <span className={styles.highlightValue}>{statistics.visitCount} lần</span>
                    </div>
                </div>
                <div className={styles.highlight}>
                    <div className={`${styles.highlightIcon} ${styles.highlightIconYellow}`}>
                        <Star />
                    </div>
                    <div className={styles.highlightContent}>
                        <span className={styles.highlightLabel}>Tổng điểm</span>
                        <span className={styles.highlightValue}>{formatPoints(customer.point)}</span>
                    </div>
                </div>
                <div className={styles.highlight}>
                    <div className={`${styles.highlightIcon} ${styles.highlightIconRed}`}>
                        <Banknote />
                    </div>
                    <div className={styles.highlightContent}>
                        <span className={styles.highlightLabel}>Tổng nợ</span>
                        <span className={styles.highlightValue}>{formatCurrency(statistics.totalOwed)}</span>
                    </div>
                </div>
            </div>

            {/* ===== 2 COLUMN LAYOUT ===== */}
            <div className={styles.layout}>
                {/* ===== LEFT: Thông tin KH ===== */}
                <div className={styles.layoutLeft}>
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <span className={styles.cardTitle}>Thông tin khách hàng</span>
                        </div>
                        <div className={styles.cardBody}>
                            {isEditing ? (
                                <div className={styles.editForm}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Tên khách hàng</label>
                                        <input
                                            type="text"
                                            className={styles.formInput}
                                            value={editForm.name}
                                            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Số điện thoại</label>
                                        <input
                                            type="tel"
                                            className={styles.formInput}
                                            value={editForm.phone}
                                            onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                                            maxLength={10}
                                        />
                                    </div>
                                    <div className={styles.formRow}>
                                        <div className={styles.formGroup}>
                                            <label className={styles.formLabel}>Điểm tích luỹ</label>
                                            <input
                                                type="number"
                                                className={styles.formInput}
                                                value={editForm.point}
                                                onChange={(e) => setEditForm((f) => ({ ...f, point: e.target.value }))}
                                            />
                                        </div>
                                        <div className={styles.formGroup}>
                                            <label className={styles.formLabel}>Loại khách hàng</label>
                                            <select
                                                className={styles.formInput}
                                                value={editForm.type}
                                                onChange={(e) => setEditForm((f) => ({ ...f, type: e.target.value }))}
                                            >
                                                {TYPE_OPTIONS.map((o) => (
                                                    <option key={o.value} value={o.value}>
                                                        {o.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Sở thích khách</label>
                                        <textarea
                                            className={styles.formTextarea}
                                            value={editForm.noteLike}
                                            onChange={(e) => setEditForm((f) => ({ ...f, noteLike: e.target.value }))}
                                            rows={2}
                                        />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.formLabel}>Ghi chú nội bộ</label>
                                        <textarea
                                            className={styles.formTextarea}
                                            value={editForm.noteInternal}
                                            onChange={(e) =>
                                                setEditForm((f) => ({ ...f, noteInternal: e.target.value }))
                                            }
                                            rows={2}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.infoGrid}>
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoIcon}>
                                            <User size={16} />
                                        </span>
                                        <div>
                                            <span className={styles.infoLabel}>Tên khách hàng</span>
                                            <span className={styles.infoValue}>{customer.name}</span>
                                        </div>
                                    </div>
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoIcon}>
                                            <Phone size={16} />
                                        </span>
                                        <div>
                                            <span className={styles.infoLabel}>Số điện thoại</span>
                                            <span className={styles.infoValue}>
                                                {canViewPhone
                                                    ? formatPhoneNumber(customer.phone)
                                                    : customer.phone
                                                      ? customer.phone.replace(/(\d{3})\d{4}(\d{3})/, '$1****$2')
                                                      : '-'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoIcon}>
                                            <Tag size={16} />
                                        </span>
                                        <div>
                                            <span className={styles.infoLabel}>Loại khách hàng</span>
                                            <span className={styles.infoValue}>
                                                {TYPE_LABELS[customer.type] || 'Khách thường'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoIcon}>
                                            <Calendar size={16} />
                                        </span>
                                        <div>
                                            <span className={styles.infoLabel}>Lần ghé gần nhất</span>
                                            <span className={styles.infoValue}>
                                                {recentBills[0]?.checkoutTime
                                                    ? formatDateTime(recentBills[0].checkoutTime)
                                                    : '-'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className={styles.infoRow}>
                                        <span className={styles.infoIcon}>
                                            <Calendar size={16} />
                                        </span>
                                        <div>
                                            <span className={styles.infoLabel}>Ngày tạo</span>
                                            <span className={styles.infoValue}>
                                                {formatDateTime(customer.createdAt || customer.createAt)}
                                            </span>
                                        </div>
                                    </div>
                                    {customer.noteLike && (
                                        <div className={styles.infoRow}>
                                            <span className={styles.infoIcon}>
                                                <Heart size={16} />
                                            </span>
                                            <div>
                                                <span className={styles.infoLabel}>Sở thích</span>
                                                <span className={styles.infoValue}>{customer.noteLike}</span>
                                            </div>
                                        </div>
                                    )}
                                    {customer.noteInternal && (
                                        <div className={styles.infoRow}>
                                            <span className={styles.infoIcon}>
                                                <StickyNote size={16} />
                                            </span>
                                            <div>
                                                <span className={styles.infoLabel}>Ghi chú nội bộ</span>
                                                <span className={styles.infoValue}>{customer.noteInternal}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* ===== RIGHT: Lịch sử ===== */}
                <div className={styles.layoutRight}>
                    {/* Tabs */}
                    <div className={styles.tabs}>
                        <button
                            className={`${styles.tab}${activeTab === 'bills' ? ` ${styles.tabActive}` : ''}`}
                            onClick={() => setActiveTab('bills')}
                        >
                            Lịch sử mua hàng
                        </button>
                        {!isKhachLe && (
                            <button
                                className={`${styles.tab}${activeTab === 'points' ? ` ${styles.tabActive}` : ''}`}
                                onClick={() => setActiveTab('points')}
                            >
                                Lịch sử điểm
                            </button>
                        )}
                        {canViewPackages && !isKhachLe && (
                            <button
                                className={`${styles.tab}${activeTab === 'packages' ? ` ${styles.tabActive}` : ''}`}
                                onClick={() => setActiveTab('packages')}
                            >
                                Thẻ gói
                            </button>
                        )}
                    </div>

                    {/* Bills tab */}
                    {activeTab === 'bills' && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardTitle}>Hoá đơn gần đây</span>
                                <button
                                    className={styles.viewAllBtn}
                                    onClick={() => navigate(`/admin/customers/${id}/history`)}
                                >
                                    Xem tất cả <ChevronRight size={16} />
                                </button>
                            </div>
                            <div className={styles.billsList}>
                                {recentBills.length === 0 ? (
                                    <div className={styles.emptyState}>Chưa có hoá đơn nào</div>
                                ) : (
                                    recentBills.map((bill) => {
                                        const statusInfo = STATUS_MAP[bill.status] || STATUS_MAP.paid;
                                        const items = bill.details || bill.items || [];
                                        return (
                                            <div
                                                key={bill.id}
                                                className={styles.billItem}
                                                onClick={() => navigate(`/admin/bills/${bill.id}`)}
                                            >
                                                <div className={styles.billItemTop}>
                                                    <div>
                                                        <span className={styles.billRelDate}>
                                                            {formatRelativeDate(bill.checkoutTime)}
                                                        </span>
                                                        <span className={styles.billMeta}>
                                                            {formatDateTime(bill.checkoutTime)} &middot;{' '}
                                                            {formatBillCode(bill)}
                                                        </span>
                                                    </div>
                                                    <span
                                                        className={`${styles.billAmount} ${bill.status !== 'paid' ? styles.billAmountUnpaid : ''}`}
                                                    >
                                                        {formatCurrency(bill.total)}
                                                    </span>
                                                </div>
                                                <div className={styles.billItemBottom}>
                                                    <span className={styles.billServices}>
                                                        {items
                                                            .slice(0, 2)
                                                            .map((it) => it.service?.name || it.name || 'Dịch vụ')
                                                            .join(', ')}
                                                        {items.length > 2 && ` +${items.length - 2}`}
                                                    </span>
                                                    <span
                                                        className={`${styles.billStatus} ${styles[`billStatus${statusInfo.class.charAt(0).toUpperCase() + statusInfo.class.slice(1)}`]}`}
                                                    >
                                                        {statusInfo.label}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}

                    {/* Points tab */}
                    {activeTab === 'points' && !isKhachLe && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardTitle}>Lịch sử điểm</span>
                                <button
                                    className={styles.viewAllBtn}
                                    onClick={() => navigate(`/admin/customers/${id}/points`)}
                                >
                                    Xem tất cả <ChevronRight size={16} />
                                </button>
                            </div>
                            <div className={styles.pointList}>
                                {filteredPoints.length === 0 ? (
                                    <div className={styles.emptyState}>Chưa có giao dịch điểm</div>
                                ) : (
                                    filteredPoints.map((t) => (
                                        <div
                                            key={t.id}
                                            className={styles.pointItem}
                                            onClick={() => t.billId && navigate(`/admin/bills/${t.billId}`)}
                                        >
                                            <div className={styles.pointItemLeft}>
                                                <span className={styles.pointBillCode}>
                                                    {t.billId ? `HD${String(t.billId).padStart(6, '0')}` : '-'}
                                                </span>
                                                <span className={styles.pointDate}>{formatDateTime(t.createdAt)}</span>
                                            </div>
                                            <div className={styles.pointItemRight}>
                                                <span
                                                    className={
                                                        t.type === 'earn' ? styles.pointEarn : styles.pointRedeem
                                                    }
                                                >
                                                    {t.type === 'earn' ? '+' : '-'}
                                                    {formatPoints(Math.abs(t.points))}
                                                </span>
                                                <span className={styles.pointBalance}>
                                                    Số dư: {formatPoints(t.balanceAfter)}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Packages tab */}
                    {activeTab === 'packages' && canViewPackages && !isKhachLe && (
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <span className={styles.cardTitle}>Thẻ gói dịch vụ</span>
                            </div>
                            <div className={styles.cardBody}>
                                <CustomerPackagesPanel customerId={id} />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Success Modal */}
            {showSuccess && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div className={styles.successIcon}>✓</div>
                        <span className={styles.successText}>{successMessage}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
